import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Youtube, Mail, Phone } from 'lucide-react';
import { SocialModal, CollaborationModal, HowItWorksModal, TikTokIcon } from './FooterModals';

const Footer = () => {
  const [footerData, setFooterData] = useState(null);
  const [siteSettings, setSiteSettings] = useState({});
  const [socialLinks, setSocialLinks] = useState({});
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [showCollaborationModal, setShowCollaborationModal] = useState(false);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [isOpeningSocial, setIsOpeningSocial] = useState(false);
  const [isOpeningCollaboration, setIsOpeningCollaboration] = useState(false);
  const [isOpeningHowItWorks, setIsOpeningHowItWorks] = useState(false);
  const [isClosingSocial, setIsClosingSocial] = useState(false);
  const [isClosingCollaboration, setIsClosingCollaboration] = useState(false);
  const [isClosingHowItWorks, setIsClosingHowItWorks] = useState(false);
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  // Fetch site settings on mount
  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/site-settings`);
        if (response.ok) {
          const settings = await response.json();
          setSiteSettings(settings);
        }
      } catch (error) {
        console.error('Error fetching site settings:', error);
      }
    };
    fetchSiteSettings();
  }, []);

  // Opening animations
  useEffect(() => {
    if (showSocialModal) {
      setTimeout(() => setIsOpeningSocial(true), 10);
    } else {
      setIsOpeningSocial(false);
    }
  }, [showSocialModal]);

  useEffect(() => {
    if (showCollaborationModal) {
      setTimeout(() => setIsOpeningCollaboration(true), 10);
    } else {
      setIsOpeningCollaboration(false);
    }
  }, [showCollaborationModal]);

  useEffect(() => {
    if (showHowItWorksModal) {
      setTimeout(() => setIsOpeningHowItWorks(true), 10);
    } else {
      setIsOpeningHowItWorks(false);
    }
  }, [showHowItWorksModal]);

  const closeSocialModal = () => {
    setIsClosingSocial(true);
    setTimeout(() => {
      setShowSocialModal(false);
      setIsClosingSocial(false);
    }, 300);
  };

  const closeCollaborationModal = () => {
    setIsClosingCollaboration(true);
    setTimeout(() => {
      setShowCollaborationModal(false);
      setIsClosingCollaboration(false);
    }, 300);
  };

  const closeHowItWorksModal = () => {
    setIsClosingHowItWorks(true);
    setTimeout(() => {
      setShowHowItWorksModal(false);
      setIsClosingHowItWorks(false);
    }, 300);
  };

  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/footer`);
        if (response.ok) {
          const data = await response.json();
          setFooterData(data);
        }
      } catch (error) {
        console.error('Error fetching footer:', error);
      }
    };
    fetchFooter();
  }, [API_BASE]);

  // Get link styling from footer data
  const linkTextColor = footerData?.link_style?.text_color || 'text-gray-900';
  const linkTextColorDark = footerData?.link_style?.text_color_dark || 'dark:text-gray-200';
  const linkFontWeight = footerData?.link_style?.font_weight || 'font-medium';
  const headingSize = footerData?.link_style?.heading_size || 'text-lg';
  const linkSize = footerData?.link_style?.link_size || 'text-sm';
  const linkClassName = `${linkTextColor} ${linkTextColorDark} ${linkFontWeight} ${linkSize} hover:text-red-600 dark:hover:text-red-400 transition-colors`;

  // Fetch social media links
  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/social-media-links`);
        if (response.ok) {
          const data = await response.json();
          setSocialLinks(data);
        }
      } catch (error) {
        console.error('Error fetching social links:', error);
      }
    };
    fetchSocialLinks();
  }, [API_BASE]);

  // Fallback to default if no data or empty sections
  const sections = footerData?.sections && Array.isArray(footerData.sections) && footerData.sections.length > 0 ? footerData.sections : [
    {
      title: 'Follow Us',
      order: 0,
      links: [
        { title: "Let's Get Social", url: '/social' },
        { title: 'Join the Community', url: '/community' },
        { title: 'Collaboration', url: '/collaboration' }
      ]
    },
    {
      title: 'Support',
      order: 1,
      links: [
        { title: 'Contact Us', url: '/contact' },
        { title: 'How It Works', url: '/how-it-works' },
        { title: 'Privacy Policy', url: '/privacy-policy' },
        { title: 'Terms & Conditions', url: '/terms-conditions' },
        { title: 'Disclaimer and Disclosure Statement', url: '/disclaimer' }
      ]
    },
    {
      title: 'Blog',
      order: 2,
      links: [
        { title: 'Blog Home', url: '/blog' },
        { title: 'Other Sport Branches', url: '/blog/category/other-sport-branches' },
        { title: 'Nutrition and Recipes', url: '/blog/category/nutrition-recipes' },
        { title: 'Training Tips', url: '/blog/category/training-tips' },
        { title: 'Muscle Building', url: '/blog/category/muscle-building' },
        { title: 'Fitness in Canada', url: '/blog/category/fitness-canada' },
        { title: 'Sport Nutrition', url: '/blog/category/sport-nutrition' }
      ]
    }
  ];

  // Social links are now fetched separately via state

  const copyrightText = siteSettings?.copyright_text || footerData?.copyright_text || '© 2025 TrainerList Inc. All Right Reserved!';

  // Sort sections by order
  const sortedSections = [...sections].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <footer className="bg-gray-100 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 mt-20">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About Us Section - NEW! */}
          <div>
            <Link 
              to="/about"
              onClick={() => window.scrollTo(0, 0)}
            >
              <h3 className={`font-bold ${headingSize} mb-4 text-gray-900 dark:text-gray-100 hover:text-red-600 dark:hover:text-red-400 transition-colors cursor-pointer`}>
                About Us
              </h3>
            </Link>
            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
              {footerData?.about_text && (
                <p className="leading-relaxed">{footerData.about_text}</p>
              )}
              {footerData?.contact_email && (
                <div className="flex items-start gap-2">
                  <Mail className="w-4 h-4 mt-1 flex-shrink-0" />
                  <a 
                    href={`mailto:${footerData.contact_email}`}
                    className="hover:text-red-600 dark:hover:text-red-400 transition-colors break-words"
                  >
                    {footerData.contact_email}
                  </a>
                </div>
              )}
              {footerData?.contact_phone && (
                <div className="flex items-start gap-2">
                  <Phone className="w-4 h-4 mt-1 flex-shrink-0" />
                  <a 
                    href={`tel:${footerData.contact_phone}`}
                    className="hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  >
                    {footerData.contact_phone}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Existing Sections */}
          {sortedSections.map((section, idx) => (
            <div key={section.title}>
              <h3 className={`font-bold ${headingSize} mb-4 text-gray-900 dark:text-gray-100`}>{section.title}</h3>
              <ul className="space-y-2 text-sm">
                {section.links && section.links.map((link) => (
                  <li key={link.title}>
                    {link.title === "Let's Get Social" ? (
                      <button
                        data-testid="footer-social-trigger"
                        onClick={() => setShowSocialModal(true)}
                        className={`${linkClassName} cursor-pointer text-left`}
                      >
                        {link.title}
                      </button>
                    ) : link.title === "Collaboration" ? (
                      <button
                        data-testid="footer-collaboration-trigger"
                        onClick={() => setShowCollaborationModal(true)}
                        className={`${linkClassName} cursor-pointer text-left`}
                      >
                        {link.title}
                      </button>
                    ) : link.title === "How It Works" ? (
                      <button
                        data-testid="footer-how-it-works-trigger"
                        onClick={() => setShowHowItWorksModal(true)}
                        className={`${linkClassName} cursor-pointer text-left`}
                      >
                        {link.title}
                      </button>
                    ) : link.url.startsWith('http') ? (
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkClassName}
                      >
                        {link.title}
                      </a>
                    ) : (
                      <Link
                        to={link.url}
                        onClick={() => window.scrollTo(0, 0)}
                        className={linkClassName}
                      >
                        {link.title}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
              
              {/* Show social media icons on first section if it's "Follow Us" */}
              {idx === 0 && section.title.toLowerCase().includes('follow') && (
                <div className="flex flex-wrap gap-3 mt-6">
                  {socialLinks.facebook && (
                    <a
                      href={socialLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-red-600 dark:hover:bg-red-600 hover:text-white flex items-center justify-center transition-all"
                      title="Facebook"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                  )}
                  {socialLinks.instagram && (
                    <a
                      href={socialLinks.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-red-700 dark:hover:bg-red-700 hover:text-white flex items-center justify-center transition-all"
                      title="Instagram"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                  )}
                  {socialLinks.twitter && (
                    <a
                      href={socialLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-blue-400 dark:hover:bg-blue-400 hover:text-white flex items-center justify-center transition-all"
                      title="Twitter / X"
                    >
                      <Twitter className="w-5 h-5" />
                    </a>
                  )}
                  {socialLinks.linkedin && (
                    <a
                      href={socialLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-blue-700 dark:hover:bg-blue-700 hover:text-white flex items-center justify-center transition-all"
                      title="LinkedIn"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                  )}
                  {socialLinks.youtube && (
                    <a
                      href={socialLinks.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-red-600 dark:hover:bg-red-600 hover:text-white flex items-center justify-center transition-all"
                      title="YouTube"
                    >
                      <Youtube className="w-5 h-5" />
                    </a>
                  )}
                  {socialLinks.tiktok && (
                    <a
                      href={socialLinks.tiktok}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-900 dark:hover:bg-gray-900 hover:text-white flex items-center justify-center transition-all"
                      title="TikTok"
                    >
                      <TikTokIcon className="w-5 h-5" />
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Resources Section - Blog & FAQ */}
          <div>
            <h3 className={`font-bold ${headingSize} mb-4 text-gray-900 dark:text-gray-100`}>
              Resources
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to="/blog"
                  onClick={() => window.scrollTo(0, 0)}
                  className={linkClassName}
                >
                  Blog
                </Link>
              </li>
              <li>
                <Link
                  to="/faq"
                  onClick={() => window.scrollTo(0, 0)}
                  className={linkClassName}
                >
                  FAQ
                </Link>
              </li>
              <li>
                <Link
                  to="/badges"
                  onClick={() => window.scrollTo(0, 0)}
                  className={linkClassName}
                >
                  Badges
                </Link>
              </li>
              <li>
                <Link
                  to="/calorie-calculator"
                  onClick={() => window.scrollTo(0, 0)}
                  className={linkClassName}
                >
                  Calorie Calculator
                </Link>
              </li>
            </ul>
          </div>

          {/* Footer Links Section - Disabled (was showing test data) */}
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-300 dark:border-gray-700 mt-8 pt-6 text-center">
          <p className="text-gray-600 dark:text-gray-400 text-sm">{copyrightText}</p>
        </div>
      </div>

      <SocialModal
        show={showSocialModal}
        isOpening={isOpeningSocial}
        isClosing={isClosingSocial}
        onClose={closeSocialModal}
        socialLinks={socialLinks}
      />

      <CollaborationModal
        show={showCollaborationModal}
        isOpening={isOpeningCollaboration}
        isClosing={isClosingCollaboration}
        onClose={closeCollaborationModal}
      />

      <HowItWorksModal
        show={showHowItWorksModal}
        isOpening={isOpeningHowItWorks}
        isClosing={isClosingHowItWorks}
        onClose={closeHowItWorksModal}
      />
    </footer>
  );
};

export default Footer;